<!--
  Passport Builder v2: header, palette sidebar, and tabbed grid canvas.

  Save order: send normalized layout (`field_groups` + `field_layouts`), persist the object type
  (`name`, map and behavior settings), then refetch and rebuild the tab strip.
-->
<script setup lang="ts">
import './assets/passport-builder.scss'
import { computed, nextTick, ref, watch } from 'vue'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { cloneDeep } from 'lodash'
import type { DragPayload, PaletteField } from '../model/types'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import type { SummaryGroupingSettings } from '../model/summaryGroupingDraft'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { useNotification } from '@/shared/ui'
import { useHttp, useTranslate } from '@/shared/lib'
import { useBuilderState } from '../model/useBuilderState'
import { useBuilderRelationRows } from '../model/useBuilderRelationRows'
import { isAutoRelationTableField, usePalette } from '../model/usePalette'
import PassportBuilderLinksModal from './PassportBuilderLinksModal.vue'
import { useTabManager } from '../model/useTabManager'
import {
  useGridOperations,
  type GridRowGroup,
} from '../model/useGridOperations'
import { parseDragTransfer, useDragDrop } from '../model/useDragDrop'
import {
  addSummaryLayoutItemFromPalette,
  addTableColumn,
  insertIndexFromPointer,
  insertIndexSummaryFromPointer,
  mergeInventoryFieldsById,
  shortFieldLabelFromDragName,
} from '../model/tableLayout'
import { findPlacedCanvasField, scopeForActiveTab, tabIdForGridItem } from '../model/tabGrid'
import { useTabPersistence } from '../model/useTabPersistence'
import { runPassportTabSavePipeline } from '../model/passportTabSavePipeline'
import {
  buildPassportLayoutPayload,
  type NegativeGroupIdMap,
} from '../model/layoutPayload'
import PassportBuilderHeader from './PassportBuilderHeader.vue'
import PassportBuilderSidebar from './PassportBuilderSidebar.vue'
import PassportBuilderCanvas from './PassportBuilderCanvas.vue'
import PassportBuilderFieldLayoutModal, {
  type PassportBuilderFieldLayoutMode,
  type PassportBuilderFieldLayoutValue,
} from './PassportBuilderFieldLayoutModal.vue'
import PassportBuilderFieldsModal from './PassportBuilderFieldsModal.vue'
import PassportBuilderSummaryGroupingModal from './PassportBuilderSummaryGroupingModal.vue'
import PassportBuilderTypeSettingsModal from './PassportBuilderTypeSettingsModal.vue'

const route = useRoute()
const http = useHttp()

const { addError, addSuccess } = useNotification()
const { trs } = useTranslate()

/** Inventory object type id (route `entity_id`). */
const objectTypeId = computed(() => route.params.entity_id as string | number)
/** Group id (route `object_id`). */
const objectId = computed(() => String(route.params.object_id ?? ''))

const builder = useBuilderState(objectTypeId, objectId)
const {
  formBase,
  fields,
  paletteFields,
  fieldGroups,
  parentFields,
  objectTypeFieldsResponse,
  objectTypeFieldsLoading,
  refetchObjectTypeFields,
  isSaving,
} = builder

/** Current normalized fields so placed cells can resolve `type` for badges. */
const allFieldsMerged = computed(() =>
  mergeInventoryFieldsById(fields.value, paletteFields.value),
)

/** Prefer route `entity_id` — it is the catalog type being edited. `formBase.id` may be a different row key. */
const expectedCatalogTypeId = computed(() => {
  const fromRoute = Number(objectTypeId.value)
  if (Number.isFinite(fromRoute) && fromRoute > 0) return fromRoute
  return Number(formBase.value?.object_type?.id ?? formBase.value?.id) || 0
})

const { rows: relationRows } = useBuilderRelationRows(
  expectedCatalogTypeId,
  objectTypeFieldsResponse,
)

const relationIds = computed<Set<number> | null>(() => {
  const rows = relationRows.value
  if (!rows.length) return null
  return new Set(rows.map((r) => r.id))
})

const AUTO_RELATION_LIST_GROUP_ID_BASE = 900_000_000

const autoRelationListGroups = computed<ObjectTypeGroup[]>(() => {
  const explicitListTargets = new Set<number>()
  for (const fg of fieldGroups.value) {
    if (String(fg.type ?? '').toUpperCase() !== 'LIST') continue
    const lid = Number(fg.list_invent_object_type_id)
    if (Number.isFinite(lid) && lid > 0) explicitListTargets.add(lid)
  }
  const map = new Map<number, ObjectTypeGroup>()
  for (const f of fields.value) {
    const targetId = Number(f.link_target_type_id)
    if (!f.is_table || !Number.isFinite(targetId) || targetId <= 0) continue
    if (explicitListTargets.has(targetId)) continue
    if (relationIds.value != null) {
      const rid = Number(f.relation_field_id)
      if (Number.isFinite(rid) && rid > 0 && !relationIds.value.has(rid)) continue
    }
    if (map.has(targetId)) continue
    map.set(targetId, {
      id: AUTO_RELATION_LIST_GROUP_ID_BASE + targetId,
      catalog_invent_objects_type_id: expectedCatalogTypeId.value,
      name: f.name?.trim() || `Перечень #${targetId}`,
      open: false,
      position: 'APPEND',
      sort: 0,
      protect: false,
      is_tab: false,
      mode: 'DEFAULT',
      type: 'LIST',
      list_invent_object_type_id: targetId,
      is_auto_relation: true,
    })
  }
  return [...map.values()]
})

const paletteFieldGroups = computed<ObjectTypeGroup[]>(() => [
  ...fieldGroups.value,
  ...autoRelationListGroups.value,
])

const palette = usePalette(
  paletteFieldGroups,
  objectTypeFieldsResponse,
  objectTypeFieldsLoading,
  refetchObjectTypeFields,
)

const tabManager = useTabManager(builder.fieldGroups, {
  resetWhen: objectTypeId,
  gridTemplate: builder.gridTemplate,
  expectedCatalogTypeId,
})
const { tabs, activeTabId } = tabManager
const tabScope = computed(() => scopeForActiveTab(tabs.value, activeTabId.value))
/** Legacy grouping mode: every server group is a canvas tab, so no LIST group is folded into general. */
const listGroupIds = computed(() => new Set<number>())
const gridOps = useGridOperations(builder.gridTemplate, tabScope, listGroupIds)
const dragDrop = useDragDrop()
const tabPersistence = useTabPersistence()

function focusPlacedField(item: GridFieldType) {
  const tabId = tabIdForGridItem(item, listGroupIds.value)
  tabManager.setActiveTab(tabId)
  nextTick(() => {
    nextTick(() => {
      requestAnimationFrame(() => {
        const idEsc = CSS.escape(String(item.id))
        const el = document.querySelector<HTMLElement>(
          `[data-pb-field-id="${idEsc}"][data-pb-field-row="${item.row}"][data-pb-field-cell="${item.cell}"]`,
        )
        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
        el?.classList.add('pb-field-card--locate-flash')
        window.setTimeout(() => el?.classList.remove('pb-field-card--locate-flash'), 1400)
      })
    })
  })
}

/** Sidebar chip click: switch to the tab that contains this field and scroll to the card. */
function onPaletteFieldActivate(field: PaletteField) {
  if (field.isTableEntry) return
  const drag = field.drag
  if (drag.list_group_id != null) return
  const id = drag.id
  if (id == null || String(id) === '') return
  const placed = findPlacedCanvasField(builder.gridTemplate.value, String(id))
  if (placed) focusPlacedField(placed)
}

type SummaryGroupingOption = { title: string; value: string | number | null }

const summaryGroupingOpen = ref(false)
const summaryGroupingTableFieldId = ref<string | null>(null)
const summaryGroupingLayoutItemId = ref<string | null>(null)

function summaryGroupingTableField() {
  const tableFieldId = summaryGroupingTableFieldId.value
  if (!tableFieldId) return null
  return builder.gridTemplate.value.find((f) => f.id === tableFieldId && f.type === 'table') ?? null
}

function summaryGroupingLayoutItem() {
  const table = summaryGroupingTableField()
  const layoutItemId = summaryGroupingLayoutItemId.value
  if (!table || !layoutItemId) return null
  return table.summary_layout?.find((item) => item.id === layoutItemId) ?? null
}

function summaryGroupingFieldItems() {
  return (summaryGroupingTableField()?.summary_layout ?? []).filter((item) => item.kind === 'field')
}

function summaryGroupingCurrentIndex() {
  const currentId = summaryGroupingLayoutItemId.value
  if (!currentId) return -1
  return summaryGroupingFieldItems().findIndex((item) => item.id === currentId)
}

function summaryGroupingListIdFromSettings(settings: Record<string, unknown> | null | undefined) {
  const raw = settings?.list_id
  if (raw == null || raw === '') return null
  return raw as string | number
}

function summaryGroupingListId() {
  const table = summaryGroupingTableField()
  const item = summaryGroupingLayoutItem()
  return summaryGroupingListIdFromSettings(item?.computed_settings) ?? table?.list_group_id ?? null
}

function summaryGroupingCurrentField() {
  const item = summaryGroupingLayoutItem()
  if (!item) return null
  return allFieldsMerged.value.find((field) => String(field.id) === String(item.ref_id)) ?? null
}

function summaryGroupingOptionFromField(field: { id: string | number; name?: string | null }) {
  return {
    title: String(field.name ?? '').trim(),
    value: field.id,
  }
}

function summaryGroupingOptionsFromMergedFields(
  listId: string | number | null,
  currentId: string | number,
) {
  if (listId == null) return []
  return allFieldsMerged.value
    .filter((field) => String(field.group ?? '') === String(listId))
    .filter((field) => String(field.id) !== String(currentId))
    .map(summaryGroupingOptionFromField)
    .filter((item) => item.title.length > 0)
}

const summaryGroupingInListItems = computed<SummaryGroupingOption[]>(() => {
  const field = summaryGroupingCurrentField()
  if (!field?.items?.length) return []
  return field.items
    .map((item) => ({
      title: item.name,
      value: item.id,
    }))
    .filter((item) => item.title.trim().length > 0)
})

/** Toolbar title when switching summary fields; empty → modal default «Сводная информация». */
const summaryGroupingToolbarTitle = computed(() => {
  const item = summaryGroupingLayoutItem()
  if (!item || item.kind !== 'field') return ''
  const custom =
    'label' in item && typeof (item as { label?: string }).label === 'string'
      ? String((item as { label: string }).label).trim()
      : ''
  if (custom) return custom
  const field = summaryGroupingCurrentField()
  const rawName = field?.name != null ? String(field.name).trim() : ''
  if (rawName) return shortFieldLabelFromDragName(rawName)
  return ''
})

function openSummaryGroupingModal(payload: { tableFieldId: string; layoutItemId: string }) {
  const table = builder.gridTemplate.value.find(
    (f) => f.id === payload.tableFieldId && f.type === 'table',
  )
  if (!table?.summary_mode) return
  const item = table.summary_layout?.find((x) => x.id === payload.layoutItemId)
  if (!item || item.kind !== 'field') return
  summaryGroupingTableFieldId.value = payload.tableFieldId
  summaryGroupingLayoutItemId.value = payload.layoutItemId
  summaryGroupingOpen.value = true
}

function closeSummaryGroupingModal() {
  summaryGroupingOpen.value = false
  summaryGroupingTableFieldId.value = null
  summaryGroupingLayoutItemId.value = null
}

function switchSummaryGroupingField(direction: 'prev' | 'next') {
  const items = summaryGroupingFieldItems()
  const currentIndex = summaryGroupingCurrentIndex()
  if (currentIndex < 0) return
  const nextIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1
  const nextItem = items[nextIndex]
  if (!nextItem) return
  summaryGroupingLayoutItemId.value = nextItem.id
}

function saveSummaryGroupingSettings(value: SummaryGroupingSettings) {
  const tableFieldId = summaryGroupingTableFieldId.value
  const layoutItemId = summaryGroupingLayoutItemId.value
  if (!tableFieldId || !layoutItemId) return
  gridOps.patchTableField(tableFieldId, (f) => {
    if (f.type !== 'table') return f
    const layout = (f.summary_layout ?? []).map((item) =>
      item.id === layoutItemId ? { ...item, computed_settings: cloneDeep(value) } : item,
    )
    return { ...f, summary_layout: layout }
  })
}

function onSummaryFieldSettings(payload: { tableFieldId: string; layoutItemId: string }) {
  openSummaryGroupingModal(payload)
}

const summaryGroupingCurrentContext = computed(() => {
  const item = summaryGroupingLayoutItem()
  const listId = summaryGroupingListId()
  if (!item) return null
  return {
    current_field_id: item.ref_id,
    list_id: listId,
  }
})

const summaryGroupingSavedSettings = computed(() => {
  const item = summaryGroupingLayoutItem()
  return (item?.computed_settings ?? {}) as Record<string, unknown>
})

const summaryGroupingSameListFieldItems = computed<SummaryGroupingOption[]>(() => {
  const table = summaryGroupingTableField()
  const item = summaryGroupingLayoutItem()
  if (!table || !item) return []

  const listId = summaryGroupingListId()
  if (listId == null) return []
  const options = new Map<string, SummaryGroupingOption>()
  for (const option of summaryGroupingOptionsFromMergedFields(listId, item.ref_id)) {
    options.set(String(option.value), option)
  }
  return [...options.values()]
})

const summaryGroupingCanSwitchPrev = computed(
  () => summaryGroupingOpen.value && summaryGroupingCurrentIndex() > 0,
)

const summaryGroupingCanSwitchNext = computed(() => {
  if (!summaryGroupingOpen.value) return false
  const items = summaryGroupingFieldItems()
  const currentIndex = summaryGroupingCurrentIndex()
  return currentIndex >= 0 && currentIndex < items.length - 1
})

const saveInFlight = ref(false)
const pendingLayoutGroupIds = ref<NegativeGroupIdMap>({})
const typeSettingsOpen = ref(false)
const ownFieldsModalOpen = ref(false)
/** «Справочники»: list_types fields (legacy fields modal). */
const referenceListTypesModalOpen = ref(false)
const linksModalOpen = ref(false)
const fieldLayoutModalOpen = ref(false)
const fieldLayoutMode = ref<PassportBuilderFieldLayoutMode>('catalog')
const pendingFieldLayoutDrop = ref<{ rowNum: number; data: DragPayload } | null>(null)
const editingFieldLayout = ref<{
  id: string
  row: number
  cell: number
  mode: PassportBuilderFieldLayoutMode
} | null>(null)
const fieldLayoutInitialValue = ref<PassportBuilderFieldLayoutValue | null>(null)
const fieldLayoutCardinality = ref<string | null>(null)
const fieldLayoutFieldInfo = ref<string | null>(null)

/** Собственные справочники группы — тот же список, что использует модалка «Справочники». */
const fieldLayoutDictionaries = computed(() => objectTypeFieldsResponse.value.dictionaries)

function openOwnFieldsModal() {
  referenceListTypesModalOpen.value = false
  ownFieldsModalOpen.value = true
}

function openReferenceFieldsModal() {
  ownFieldsModalOpen.value = false
  referenceListTypesModalOpen.value = true
}

/** "Собственные поля" и "Справочники": сохранить только текущий draft, без сохранения других табов и переходов. 
Если есть несохранённые табы, сохранить их тоже. */
async function persistFieldsModalSave(next: ObjectInventoryField[]): Promise<boolean> {
  fields.value = next
  try {
    const catalogId = Number(expectedCatalogTypeId.value)
    if (tabManager.isTabsDirty.value) {
      if (!catalogId) {
        addError(trs('request.unknown_error'))
        return false
      }
      await runPassportTabSavePipeline({
        persistence: tabPersistence,
        catalogTypeId: catalogId,
        fieldGroups: builder.fieldGroups,
        refetch: () => builder.refetch(),
        tabManager,
      })
    }
    const ok = await builder.save(valid.value, { navigate: false })
    if (!ok) return false
    return true
  } catch (error: unknown) {
    const err = error as { response?: { data?: { data?: { msg?: string } } } }
    addError(trs(err?.response?.data?.data?.msg || 'request.unknown_error'))
    return false
  }
}

/**
 * Синхронизирует новые собственные поля с локальной палитрой конструктора.
 */
function handleOwnFieldsSaved(nextOwnFields: ObjectInventoryField[]) {
  const persistentOtherFields = fields.value.filter((field) => {
    if (field.type === 'list_types') return true
    return isAutoRelationTableField(field)
  })
  fields.value = [...persistentOtherFields, ...nextOwnFields]
}

/**
 * Синхронизирует новые поля-справочники с локальными list_types ветками.
 */
function handleCatalogFieldsSaved(nextCatalogFields: ObjectInventoryField[]) {
  const persistentOtherFields = fields.value.filter((field) => field.type !== 'list_types')
  fields.value = [...persistentOtherFields, ...nextCatalogFields]
}

const { rowsGrouped } = gridOps
const { dropTargetRow } = dragDrop

/** Active drag for row/table UI (dragover often cannot read dataTransfer). */
const dragPayloadActive = computed<DragPayload | null>(() => dragDrop.dragData.value ?? null)

const isDirty = computed(() => builder.isDirty.value || tabManager.isTabsDirty.value)
const isSavingUi = computed(() => isSaving.value || saveInFlight.value)

const valid = ref(true)

watch(
  () => objectTypeId.value,
  () => {
    pendingLayoutGroupIds.value = {}
  },
)

watch(
  () => fieldLayoutModalOpen.value,
  (open) => {
    if (open) return
    pendingFieldLayoutDrop.value = null
    editingFieldLayout.value = null
    fieldLayoutInitialValue.value = null
    fieldLayoutCardinality.value = null
    dragDrop.endDrag()
  },
)

const handleSave = async () => {
  if (!valid.value) return
  saveInFlight.value = true
  try {
    const catalogId = Number(expectedCatalogTypeId.value)
    if (!catalogId) {
      addError(trs('request.unknown_error'))
      return
    }

    const payload = buildPassportLayoutPayload({
      catalogTypeId: catalogId,
      fieldGroups: builder.fieldGroups.value,
      tabs: tabs.value,
      gridTemplate: builder.gridTemplate.value,
      pendingGroupIds: pendingLayoutGroupIds.value,
      deletedGroupIds: tabManager.pendingDeletedServerGroupIds.value,
    })

    await http.put(`/object-types/${catalogId}/layout`, payload)

    const typeSaved = await builder.save(valid.value, {
      navigate: false,
      notify: false,
    })
    if (!typeSaved) return

    await builder.refetch()
    await nextTick()
    tabManager.afterSaveRefetch(builder.fieldGroups.value)
    pendingLayoutGroupIds.value = {}
    builder.markSaved()
    tabManager.markSaved()
    addSuccess(trs('itf.saved'))
  } catch (error: unknown) {
    const err = error as { response?: { data?: { data?: { msg?: string } } } }
    addError(trs(err?.response?.data?.data?.msg || 'request.unknown_error'))
  } finally {
    saveInFlight.value = false
  }
}

function onDragOverRow(r: number) {
  dropTargetRow.value = r
}

/** Drop target may be `.pb-grid-row` (row chrome) — still use the matching table block rect for X → column index. */
function listPaletteTableBlockEl(event: DragEvent, tableId: string | number): HTMLElement | null {
  const t = event.target as HTMLElement | undefined
  if (!t) return null
  const fromBlock = t.closest<HTMLElement>('.pb-table-block')
  if (fromBlock) return fromBlock
  const row = t.closest<HTMLElement>('.pb-grid-row')
  if (!row) return null
  const id = String(tableId)
  const byId = row.querySelector<HTMLElement>(`[data-pb-table-id="${CSS.escape(id)}"]`)
  if (byId) return byId
  return row.querySelector<HTMLElement>('.pb-table-block')
}

/** LIST table matching uses `list_group_id` on drag; some palette rows omit it — recover from inventory `group`. */
function resolvePaletteFieldListGroupId(data: DragPayload): number | null {
  // Only «Перечни» palette fields use list_group_id for column drops. `f.group` on inventory is a
  // different concept (tab/field group) and must not trigger the LIST table branch (see usePalette toFieldDrag).
  if (data.field.palette_source !== 'list') return null
  const raw = data.field.list_group_id
  if (raw != null && Number.isFinite(Number(raw))) return Number(raw)
  const invKey = data.field.inventory_field_id ?? data.field.id
  const inv = allFieldsMerged.value.find((x) => String(x.id) === String(invKey))
  if (inv?.group != null && Number.isFinite(Number(inv.group))) return Number(inv.group)
  return null
}

function needsFieldLayoutModal(data: DragPayload): PassportBuilderFieldLayoutMode | null {
  if (data.source !== 'palette' || data.itemType !== 'field') return null
  if (data.field.palette_source === 'parent') return 'catalog'
  if (data.field.palette_source === 'element') return 'reference'
  if (data.field.palette_source === 'own') {
    const inventoryFieldId = data.field.inventory_field_id ?? data.field.id
    const field = allFieldsMerged.value.find(
      (item) => String(item.id) === String(inventoryFieldId),
    )
    if (field?.type?.toLowerCase() === 'computed') return 'computed'
  }
  return null
}

function isRepeatablePaletteField(data: DragPayload): boolean {
  if (data.source !== 'palette' || data.itemType !== 'field') return false
  return data.field.palette_source === 'parent' || data.field.palette_source === 'element'
}

function focusExistingPaletteField(data: DragPayload): boolean {
  if (isRepeatablePaletteField(data)) return false

  const rawId = data.field.id
  if (rawId == null || String(rawId) === '') return false

  const placed = findPlacedCanvasField(builder.gridTemplate.value, String(rawId))
  if (!placed) return false

  focusPlacedField(placed)
  return true
}

function placePaletteField(
  rowNum: number,
  data: DragPayload,
  layout?: PassportBuilderFieldLayoutValue,
) {
  if (focusExistingPaletteField(data)) return

  const rawId = data.field.id
  const sourceFieldId = rawId != null && String(rawId) !== '' ? String(rawId) : crypto.randomUUID()
  const id = isRepeatablePaletteField(data)
    ? `${data.field.palette_source}:${sourceFieldId}:${crypto.randomUUID()}`
    : sourceFieldId
  gridOps.smartPlaceField(rowNum, {
    id,
    name: data.field.name,
    type: 'field',
    inventory_type: data.field.inventory_type,
    inventory_field_id: data.field.inventory_field_id ?? sourceFieldId,
    length: layout?.view_type === 'table' ? 12 : 4,
    ...(data.field.nested_binding ? { nested_binding: data.field.nested_binding } : {}),
    ...(data.field.palette_source ? { palette_source: data.field.palette_source } : {}),
    ...(data.field.list_group_id != null ? { list_group_id: data.field.list_group_id } : {}),
    ...(data.field.link_field_id != null ? { link_field_id: data.field.link_field_id } : {}),
    ...(layout
      ? {
          view_type: layout.view_type,
          pseudo_sql: layout.pseudo_sql,
          filter_map: layout.filter_map,
          filter_search: layout.filter_search,
          filter_dictionary_id: layout.filter_dictionary_id,
        }
      : {}),
  })
}

/**
 * Возвращает кратность связи для поля, открытого в настройках отображения.
 */
function resolveFieldLayoutCardinality(field: {
  id: string | number
  inventory_field_id?: string | number
}): string | null {
  const inventoryFieldId = field.inventory_field_id ?? field.id
  return (
    allFieldsMerged.value.find((item) => String(item.id) === String(inventoryFieldId))
      ?.cardinality ?? null
  )
}

function gridFieldInfoLabel(item: GridFieldType): string | null {
  const fieldId = item.inventory_field_id ?? parseLeafFieldId(item.id)
  if (fieldId == null) return null
  const meta = allFieldsMerged.value.find((f) => String(f.id) === String(fieldId))
  const name = meta?.name ?? item.name
  return `${fieldId} | ${name}`
}

function parseLeafFieldId(id: string): string | number | null {
  const hashIdx = id.lastIndexOf('#f:')
  if (hashIdx >= 0) return id.slice(hashIdx + 3)
  const numeric = Number(id)
  if (Number.isFinite(numeric) && numeric > 0) return numeric
  return null
}

function openFieldLayoutModal(rowNum: number, data: DragPayload, mode: PassportBuilderFieldLayoutMode) {
  pendingFieldLayoutDrop.value = { rowNum, data }
  editingFieldLayout.value = null
  fieldLayoutInitialValue.value = null
  fieldLayoutCardinality.value = resolveFieldLayoutCardinality(data.field)
  fieldLayoutFieldInfo.value = null
  fieldLayoutMode.value = mode
  fieldLayoutModalOpen.value = true
}

function openExistingFieldLayoutModal(payload: {
  id: string
  row: number
  cell: number
  mode: PassportBuilderFieldLayoutMode
}) {
  const item = builder.gridTemplate.value.find(
    (field) =>
      field.id === payload.id &&
      field.row === payload.row &&
      field.cell === payload.cell,
  )
  if (!item) return

  editingFieldLayout.value = payload
  pendingFieldLayoutDrop.value = null
  fieldLayoutMode.value = payload.mode
  fieldLayoutCardinality.value = resolveFieldLayoutCardinality(item)
  fieldLayoutFieldInfo.value = gridFieldInfoLabel(item)
  fieldLayoutInitialValue.value = {
    view_type: item.view_type ?? (item.type === 'table' ? 'table' : 'value'),
    pseudo_sql: item.pseudo_sql ?? '',
    filter_map: Boolean(item.filter_map),
    filter_search: Boolean(item.filter_search),
    filter_dictionary_id: item.filter_dictionary_id ?? null,
  }
  fieldLayoutModalOpen.value = true
}

function saveFieldLayout(value: PassportBuilderFieldLayoutValue) {
  const editing = editingFieldLayout.value
  if (editing) {
    if (value.view_type === 'table') {
      const existing = builder.gridTemplate.value.find(
        (field) =>
          field.id === editing.id &&
          field.row === editing.row &&
          field.cell === editing.cell,
      )
      if (existing) {
        gridOps.removeField(existing.id, existing.row, existing.cell)
        gridOps.smartPlaceField(editing.row, {
          id: existing.id,
          name: existing.name,
          type: existing.type,
          inventory_type: existing.inventory_type,
          inventory_field_id: existing.inventory_field_id,
          field_layout_id: existing.field_layout_id,
          nested_binding: existing.nested_binding,
          palette_source: existing.palette_source,
          length: 12,
          list_group_id: existing.list_group_id,
          link_field_id: existing.link_field_id,
          view_type: value.view_type,
          pseudo_sql: value.pseudo_sql,
          filter_map: value.filter_map,
          filter_search: value.filter_search,
          filter_dictionary_id: value.filter_dictionary_id,
        })
      }
      editingFieldLayout.value = null
      fieldLayoutInitialValue.value = null
      return
    }

    builder.gridTemplate.value = builder.gridTemplate.value.map((field) =>
      field.id === editing.id &&
      field.row === editing.row &&
      field.cell === editing.cell
        ? {
            ...field,
            view_type: value.view_type,
            pseudo_sql: value.pseudo_sql,
            filter_map: value.filter_map,
            filter_search: value.filter_search,
            filter_dictionary_id: value.filter_dictionary_id,
          }
        : field,
    )
    editingFieldLayout.value = null
    fieldLayoutInitialValue.value = null
    return
  }

  const pending = pendingFieldLayoutDrop.value
  if (!pending) return

  placePaletteField(pending.rowNum, pending.data, value)
  pendingFieldLayoutDrop.value = null
  dragDrop.endDrag()
}

/** Saved templates sometimes omit `list_group_id` on table blocks — match single orphan table in the row. */
function findListTableForPaletteDrop(
  row: GridRowGroup | undefined,
  paletteListGid: number,
): GridFieldType | undefined {
  if (!row?.items?.length) return undefined
  const tables = row.items.filter((f) => f.type === 'table')
  const exact = tables.find(
    (f) => f.list_group_id != null && String(f.list_group_id) === String(paletteListGid),
  )
  if (exact) return exact
  if (tables.length === 1 && tables[0].list_group_id == null) return tables[0]
  return undefined
}

function withTableListGroupIfMissing(f: GridFieldType, gid: number): GridFieldType {
  if (f.type !== 'table') return f
  if (f.list_group_id != null) return f
  return { ...f, list_group_id: gid }
}

const handleDrop = (rowNum: number, event?: DragEvent) => {
  const mem = dragDrop.dragData.value
  const parsed = event ? parseDragTransfer(event) : null
  const data = mem ?? parsed
  if (!data) return

  const paletteListGid =
    data.source === 'palette' && data.itemType === 'field'
      ? resolvePaletteFieldListGroupId(data)
      : null

  if (data.source === 'grid' && data.itemType === 'summary-item') {
    dragDrop.endDrag()
    return
  }

  if (data.source === 'palette' && data.itemType === 'field' && paletteListGid != null) {
    const row = rowsGrouped.value.find((g) => g.row === rowNum)
    const table = findListTableForPaletteDrop(row, paletteListGid)
    if (table) {
      const fieldId = data.field.inventory_field_id ?? data.field.id
      const label = shortFieldLabelFromDragName(data.field.name)
      const nestedBinding = data.field.nested_binding
      if (table.summary_mode) {
        if (event) {
          const block = listPaletteTableBlockEl(event, table.id)
          if (block) {
            const summaryEl = block.querySelector<HTMLElement>('.pb-summary-subgrid')
            const rect = (summaryEl ?? block).getBoundingClientRect()
            const x = event.clientX - rect.left
            const n = table.summary_layout?.length ?? 0
            const insertIdx = insertIndexSummaryFromPointer(x, rect.width, n)
            gridOps.patchTableField(table.id, (f) =>
              addSummaryLayoutItemFromPalette(
                withTableListGroupIfMissing(f, paletteListGid),
                fieldId,
                insertIdx,
                nestedBinding,
                label,
              ),
            )
          } else {
            const n = table.summary_layout?.length ?? 0
            gridOps.patchTableField(table.id, (f) =>
              addSummaryLayoutItemFromPalette(
                withTableListGroupIfMissing(f, paletteListGid),
                fieldId,
                n,
                nestedBinding,
                label,
              ),
            )
          }
        } else {
          const n = table.summary_layout?.length ?? 0
          gridOps.patchTableField(table.id, (f) =>
            addSummaryLayoutItemFromPalette(
              withTableListGroupIfMissing(f, paletteListGid),
              fieldId,
              n,
              nestedBinding,
              label,
            ),
          )
        }
      } else if (event) {
        const block = listPaletteTableBlockEl(event, table.id)
        if (block) {
          const rect = block.getBoundingClientRect()
          const x = event.clientX - rect.left
          const insertIdx = insertIndexFromPointer(x, rect.width, table)
          gridOps.patchTableField(table.id, (f) =>
            addTableColumn(
              withTableListGroupIfMissing(f, paletteListGid),
              fieldId,
              label,
              insertIdx,
              nestedBinding,
            ),
          )
        } else {
          const colCount = table.table_columns?.length ?? 0
          gridOps.patchTableField(table.id, (f) =>
            addTableColumn(
              withTableListGroupIfMissing(f, paletteListGid),
              fieldId,
              label,
              colCount,
              nestedBinding,
            ),
          )
        }
      } else {
        const colCount = table.table_columns?.length ?? 0
        gridOps.patchTableField(table.id, (f) =>
          addTableColumn(
            withTableListGroupIfMissing(f, paletteListGid),
            fieldId,
            label,
            colCount,
            nestedBinding,
          ),
        )
      }
    }
    dragDrop.endDrag()
    return
  }

  if (data.source === 'grid') {
    const r = data.field.row
    const c = data.field.cell
    const existing = builder.gridTemplate.value.find((f) => {
      if (f.id !== data.field.id) return false
      if (r != null && c != null) {
        return f.row === r && f.cell === c
      }
      return true
    })
    if (!existing) {
      dragDrop.endDrag()
      return
    }
    gridOps.removeField(data.field.id, data.field.row, data.field.cell)
    gridOps.smartPlaceField(rowNum, {
      id: data.field.id,
      name: data.field.name,
      type: existing.type,
      inventory_type: existing.inventory_type,
      inventory_field_id: existing.inventory_field_id,
      field_layout_id: existing.field_layout_id,
      nested_binding: existing.nested_binding,
      palette_source: existing.palette_source,
      length: existing.length,
      list_group_id: existing.list_group_id,
      link_field_id: existing.link_field_id,
      view_type: existing.view_type,
      pseudo_sql: existing.pseudo_sql,
      filter_map: existing.filter_map,
      filter_search: existing.filter_search,
      filter_dictionary_id: existing.filter_dictionary_id,
    })
  } else if (data.itemType === 'heading') {
    dragDrop.endDrag()
    return
  } else if (data.itemType === 'table') {
    gridOps.addTableBlock(rowNum, {
      name: data.field.name,
      list_group_id: data.field.list_group_id,
    })
  } else {
    const layoutMode = needsFieldLayoutModal(data)
    if (layoutMode) {
      if (focusExistingPaletteField(data)) {
        dragDrop.endDrag()
        return
      }
      openFieldLayoutModal(rowNum, data, layoutMode)
      return
    }

    placePaletteField(rowNum, data)
  }
  dragDrop.endDrag()
}

const handleReorder = (
  rowNum: number,
  payload: { id: string; row: number; cell: number; targetIndex: number },
) => {
  gridOps.reorderInRow(rowNum, payload.id, payload.targetIndex, {
    row: payload.row,
    cell: payload.cell,
  })
}

onBeforeRouteLeave((_to, _from, next) => {
  if (isDirty.value) {
    const leave = window.confirm('Есть несохранённые изменения. Покинуть страницу?')
    next(leave)
  } else {
    next()
  }
})
</script>

<template>
  <!-- Modals outside .pb-page (overflow hidden) + teleported to body with high z-index in shell. -->
  <div class="pb-page">
    <PassportBuilderHeader
      :form-base="formBase"
      :is-saving="isSavingUi"
      :is-dirty="isDirty"
      @save="handleSave"
      @open-settings="typeSettingsOpen = true"
    />

    <div class="pb-main">
      <PassportBuilderSidebar
        :palette="palette"
        :drag-drop="dragDrop"
        @palette-field-activate="onPaletteFieldActivate"
        @open-own-fields="openOwnFieldsModal"
        @open-reference-fields="openReferenceFieldsModal"
        @open-links="linksModalOpen = true"
      />
      <PassportBuilderCanvas
        :tabs="tabs"
        :active-tab-id="activeTabId"
        :rows="rowsGrouped"
        :drop-target-row="dropTargetRow"
        :all-fields="allFieldsMerged"
        :drag-payload="dragPayloadActive"
        :catalog-type-id="expectedCatalogTypeId"
        @update:active-tab-id="tabManager.setActiveTab"
        @add-tab="tabManager.addTab('Новая вкладка')"
        @remove-tab="tabManager.removeTab"
        @rename-tab="(id: string, label: string) => tabManager.renameTab(id, label)"
        @reorder-tabs="(a: string, b: string) => tabManager.reorderTabsByDrop(a, b)"
        @drop="handleDrop"
        @remove-field="(p) => gridOps.removeField(p.id, p.row, p.cell)"
        @resize-field="(p) => gridOps.resizeField(p.id, p.newSpan, p.row, p.cell)"
        @rename-heading="(p) => gridOps.renameItemName(p.id, p.name, p.row, p.cell)"
        @rename-field="(p) => gridOps.renameItemName(p.id, p.name, p.row, p.cell)"
        @field-settings="openExistingFieldLayoutModal"
        @remove-row="gridOps.removeRow"
        @add-row="gridOps.addRow"
        @drag-field-start="(payload, event) => dragDrop.startDrag(payload, event)"
        @drag-over-row="onDragOverRow"
        @reorder="handleReorder"
        @patch-table-field="(id, u) => gridOps.patchTableField(id, u)"
        @builder-drag-end="dragDrop.endDrag()"
        @summary-field-settings="onSummaryFieldSettings"
      />
    </div>
  </div>

  <PassportBuilderTypeSettingsModal
    v-if="formBase"
    v-model="typeSettingsOpen"
    :form-base="formBase"
  />

  <PassportBuilderFieldsModal
    v-model="ownFieldsModalOpen"
    :title="trs('itf.fields_settings')"
    mode="exclude_list_types"
    :fields="fields"
    :parent-fields="parentFields"
    :field-groups="fieldGroups"
    :object-type-id="expectedCatalogTypeId"
    :object-type-fields-response="objectTypeFieldsResponse"
    :object-type-fields-loading="objectTypeFieldsLoading"
    :refetch-object-type-fields="refetchObjectTypeFields"
    :persist-fn="persistFieldsModalSave"
    @fields-saved="handleOwnFieldsSaved"
  />

  <PassportBuilderFieldsModal
    v-model="referenceListTypesModalOpen"
    :title="trs('itf.references_section')"
    mode="list_types_only"
    :fields="fields"
    :parent-fields="parentFields"
    :field-groups="fieldGroups"
    :object-type-id="expectedCatalogTypeId"
    :object-type-fields-response="objectTypeFieldsResponse"
    :object-type-fields-loading="objectTypeFieldsLoading"
    :refetch-object-type-fields="refetchObjectTypeFields"
    :persist-fn="persistFieldsModalSave"
    @fields-saved="handleCatalogFieldsSaved"
  />

  <PassportBuilderLinksModal
    v-if="expectedCatalogTypeId > 0"
    v-model="linksModalOpen"
    :from-type-id="expectedCatalogTypeId"
    :object-type-fields-response="objectTypeFieldsResponse"
    :object-type-fields-loading="objectTypeFieldsLoading"
    :refetch-fn="
      async () => {
        await refetchObjectTypeFields()
      }
    "
  />

  <PassportBuilderFieldLayoutModal
    v-model="fieldLayoutModalOpen"
    :mode="fieldLayoutMode"
    :initial-value="fieldLayoutInitialValue"
    :relation-cardinality="fieldLayoutCardinality"
    :field-info="fieldLayoutFieldInfo"
    :dictionaries="fieldLayoutDictionaries"
    @save="saveFieldLayout"
  />

  <PassportBuilderSummaryGroupingModal
    v-if="summaryGroupingCurrentContext"
    v-model="summaryGroupingOpen"
    :title="summaryGroupingToolbarTitle"
    :context="summaryGroupingCurrentContext"
    :saved-settings="summaryGroupingSavedSettings"
    :same-list-field-items="summaryGroupingSameListFieldItems"
    :in-list-items="summaryGroupingInListItems"
    :can-switch-prev="summaryGroupingCanSwitchPrev"
    :can-switch-next="summaryGroupingCanSwitchNext"
    @close="closeSummaryGroupingModal"
    @save="saveSummaryGroupingSettings"
    @switch="switchSummaryGroupingField"
  />
</template>
